import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ensureSessionHeaders } from '../lib/serverSession';
import { API_BASE_URL } from '../constants';

interface ZimbabweAwaitsPageProps {
  onPurchaseShares?: () => void;
  onRallyTeam?: () => void;
  currentUsername?: string;
}

interface PoolTeam {
  rank?: number;
  team_id: number;
  team_name: string;
  leader_username: string;
  total_volume_usd?: number;
  raw_volume_usd?: number;
  leg_count?: number;
  total_shares?: number;
}

interface ViewerLeg {
  userId: number;
  username: string;
  volume: number;
  shares: number;
}

interface Viewer {
  hasPoolTeam: boolean;
  teamName?: string | null;
  rawVolume: number;
  countedVolume: number;
  legCount: number;
  counts: boolean;
  minLegs: number;
  rank: number | null;
  legs: ViewerLeg[];
}

const PAGE_SIZE = 8;

function money(value: number) {
  return `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function legColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${hash % 360} 70% 55%)`;
}

export const ZimbabweAwaitsPage: React.FC<ZimbabweAwaitsPageProps> = ({
  onPurchaseShares,
  onRallyTeam,
  currentUsername
}) => {
  const [teams, setTeams] = useState<PoolTeam[]>([]);
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [minLegs, setMinLegs] = useState(2);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isTestWindow, setIsTestWindow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(true);
  const [page, setPage] = useState(0);

  const loadTopTeams = useCallback(async () => {
    try {
      setError(null);
      const headers = await ensureSessionHeaders();
      const response = await fetch(`${API_BASE_URL}/api/teams/zimbabwe-leaderboard?limit=10`, { headers });
      if (!response.ok) {
        throw new Error(`Leaderboard unavailable (${response.status})`);
      }
      const data = await response.json();
      setTeams((data.leaderboard || []).slice(0, 10));
      setViewer(data.viewer || null);
      setMinLegs(Number(data.minLegs) || 2);
      setStartDate(data.startDate || '');
      setEndDate(data.endDate || '');
      setIsTestWindow(!!data.isTestWindow);
    } catch (err: any) {
      setError(err?.message || 'Could not load the period ranking.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTopTeams();
    const timer = setInterval(loadTopTeams, 60000);
    return () => clearInterval(timer);
  }, [loadTopTeams]);

  const legs = useMemo(() => {
    const rows = viewer?.legs || [];
    return [...rows].sort((a, b) => b.volume - a.volume || a.username.localeCompare(b.username));
  }, [viewer]);

  const pageCount = Math.max(1, Math.ceil(legs.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = legs.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const needed = Math.max(0, minLegs - (viewer?.legCount || 0));

  return (
    <div className="space-y-6">
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, rgba(18,14,6,0.98) 0%, rgba(28,20,8,0.95) 45%, rgba(12,10,4,0.98) 100%)',
          border: '1px solid rgba(255,215,0,0.28)',
          boxShadow: '0 18px 40px rgba(0,0,0,0.45)'
        }}
      >
        <div className="px-6 py-8 md:px-10 md:py-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-amber-200/80 text-sm tracking-[0.28em] uppercase mb-3">
              Team Leader Competition
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold text-[#FFD700] leading-tight mb-3">
              ZIMBABWE AWAITS!
            </h1>
            <h2 className="text-xl md:text-2xl text-white font-semibold leading-snug">
              10 Team Leaders. An all-expenses-paid trip. A 10% share bonus.
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setShowRules((open) => !open)}
            className="text-xs text-gray-300 hover:text-yellow-300 border border-yellow-700/40 hover:border-yellow-400 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap"
          >
            {showRules ? 'Hide how ranking works' : 'How ranking works'}
          </button>
        </div>
      </div>

      {showRules && (
        <div className="rounded-xl border border-yellow-700/30 p-5 space-y-4" style={{ background: 'linear-gradient(135deg, rgba(120,80,0,0.18), rgba(20,40,80,0.18))' }}>
          <h2 className="text-sm font-bold text-yellow-300">How a place is counted</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border border-cyan-700/30 bg-cyan-950/20 p-3">
              <h3 className="text-cyan-300 font-semibold mb-1">1. A leg is one direct branch</h3>
              <p className="text-gray-300">A leg is one person you referred, plus everyone under that person. People are not moved into new groups.</p>
            </div>
            <div className="rounded-lg border border-emerald-700/30 bg-emerald-950/20 p-3">
              <h3 className="text-emerald-300 font-semibold mb-1">2. The same team volume as the daily pool</h3>
              <p className="text-gray-300">This uses paid team volume from {startDate || '13 September 2026'} to {endDate || '5 November 2026'}. Your own purchase is not a leg.</p>
            </div>
            <div className="rounded-lg border border-orange-700/30 bg-orange-950/20 p-3">
              <h3 className="text-orange-300 font-semibold mb-1">3. At least {minLegs} legs</h3>
              <p className="text-gray-300">Volume counts toward the top 10 only when it comes from {minLegs} or more separate legs. One leg, however large, does not take a place.</p>
            </div>
            <div className="rounded-lg border border-fuchsia-700/30 bg-fuchsia-950/20 p-3">
              <h3 className="text-fuchsia-300 font-semibold mb-1">4. A line of accounts is one leg</h3>
              <p className="text-gray-300">Five accounts under each other, with the sales on the bottom account, are one leg for each leader above that sale. That sale does not place all five.</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">This page reads the ranking. It does not pay USDT and it does not add shares. A tie goes to the earlier purchase.</p>
        </div>
      )}

      {loading && teams.length === 0 ? (
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Checking legs for {startDate || 'the ranking window'}…</p>
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-700/40 bg-red-950/30 p-4 text-red-200">{error}</div>
      ) : (
        <>
          {viewer && (
            <div className="rounded-xl border border-gray-700/40 p-5 space-y-4" style={{ background: 'rgba(20,20,20,0.6)' }}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h3 className="text-white font-bold">Your calculation</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    {viewer.hasPoolTeam
                      ? (viewer.counts
                        ? `Your volume comes from ${viewer.legCount} legs, so it counts.${viewer.rank ? (viewer.rank <= 10 ? ` You are rank ${viewer.rank}.` : ` You are rank ${viewer.rank}, outside the displayed top 10.`) : ' You are outside the current top 10.'}`
                        : `You have ${viewer.legCount} leg${viewer.legCount === 1 ? '' : 's'} with volume. You need ${needed} more before this volume counts.`)
                      : 'Register a Daily Performance Pool team to appear in this ranking.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setLoading(true); loadTopTeams(); }}
                  className="text-xs px-3 py-1.5 rounded-lg border border-yellow-500/30 text-[#FFD700] hover:bg-yellow-500/10"
                >
                  Refresh
                </button>
              </div>

              {viewer.hasPoolTeam && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-xl p-4 border border-yellow-700/30" style={{ background: 'linear-gradient(135deg, rgba(100,70,0,0.2), rgba(20,20,20,0.4))' }}>
                      <div className="text-gray-400 text-xs">Volume that counts</div>
                      <div className="text-2xl font-bold text-yellow-300">{money(viewer.countedVolume)}</div>
                    </div>
                    <div className="rounded-xl p-4 border border-cyan-700/30 bg-cyan-950/20">
                      <div className="text-gray-400 text-xs">All volume under you</div>
                      <div className="text-2xl font-bold text-cyan-300">{money(viewer.rawVolume)}</div>
                    </div>
                    <div className="rounded-xl p-4 border border-emerald-700/30 bg-emerald-950/20">
                      <div className="text-gray-400 text-xs">Legs with volume</div>
                      <div className="text-2xl font-bold text-emerald-300">{viewer.legCount} / {minLegs}</div>
                    </div>
                    <div className="rounded-xl p-4 border border-fuchsia-700/30 bg-fuchsia-950/20">
                      <div className="text-gray-400 text-xs">Your pool team</div>
                      <div className="text-lg font-bold text-fuchsia-200 truncate">{viewer.teamName || 'Pool team'}</div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-700/40 overflow-hidden" style={{ background: 'rgba(15,18,28,0.7)' }}>
                    <div className="px-5 py-3 border-b border-gray-700/40 flex items-center justify-between">
                      <h3 className="text-sm font-bold text-white">Your legs</h3>
                      <span className="text-xs text-gray-400">{legs.length} with volume in this window</span>
                    </div>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {pageRows.map((leg, index) => {
                        const name = leg.username || `User ${leg.userId}`;
                        const stripe = legColor(name);
                        const rank = safePage * PAGE_SIZE + index + 1;
                        return (
                          <div key={leg.userId} className="rounded-xl border border-gray-700/40 bg-gray-900/40 p-4" style={{ borderLeft: `4px solid ${stripe}` }}>
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <div className="text-xs text-gray-500">Leg {rank}</div>
                                <div className="font-semibold text-white">{name}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold text-yellow-300">{money(leg.volume)}</div>
                                <div className="text-[10px] uppercase text-gray-500">{leg.shares.toLocaleString()} shares</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {pageRows.length === 0 && <p className="text-gray-400 text-sm">No paid volume is sitting on a direct leg in this window yet.</p>}
                    </div>
                    {legs.length > PAGE_SIZE && (
                      <div className="px-4 pb-4 flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => setPage((current) => Math.max(0, current - 1))}
                          disabled={safePage === 0}
                          className="px-3 py-1.5 text-xs rounded-lg border border-gray-600 text-gray-200 hover:border-yellow-400 disabled:opacity-30"
                        >Previous</button>
                        <span className="text-xs text-gray-400">Page {safePage + 1} of {pageCount}</span>
                        <button
                          type="button"
                          onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
                          disabled={safePage >= pageCount - 1}
                          className="px-3 py-1.5 text-xs rounded-lg border border-gray-600 text-gray-200 hover:border-yellow-400 disabled:opacity-30"
                        >Next</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-700">
              <h3 className="text-[#FFD700] font-bold text-lg">Current Top 10</h3>
              <p className="text-gray-400 text-sm mt-1">
                Ranked by volume from at least {minLegs} direct legs, {startDate || '—'} to {endDate || '—'}
                {isTestWindow ? ' (test window — not the live 13 Sep–5 Nov ranking).' : '.'}
              </p>
            </div>
            {teams.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">No team has volume from {minLegs} legs in this window yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-[11px] uppercase border-b border-gray-700">
                      <th className="px-4 py-3 text-left w-16">Rank</th>
                      <th className="px-4 py-3 text-left">Team</th>
                      <th className="px-4 py-3 text-left hidden sm:table-cell">Team Leader</th>
                      <th className="px-4 py-3 text-right">Legs</th>
                      <th className="px-4 py-3 text-right">Volume that counts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((team, idx) => {
                      const rank = team.rank || idx + 1;
                      const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
                      const isMe = !!currentUsername && team.leader_username === currentUsername;
                      return (
                        <tr
                          key={team.team_id}
                          className={`border-b border-gray-800/60 ${isMe ? 'bg-yellow-900/15' : ''}`}
                        >
                          <td className="px-4 py-3 font-bold text-white">{medal}</td>
                          <td className="px-4 py-3">
                            <span className={`font-medium ${isMe ? 'text-yellow-400' : 'text-white'}`}>{team.team_name}</span>
                            {isMe && <span className="text-[10px] text-yellow-600 ml-2 font-semibold">(YOU)</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-400 hidden sm:table-cell">{team.leader_username}</td>
                          <td className="px-4 py-3 text-right font-mono text-emerald-300">{team.leg_count ?? '—'}</td>
                          <td className="px-4 py-3 text-right font-mono text-[#FFD700]">{money(Number(team.total_volume_usd) || 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 md:p-8">
        <p className="text-gray-200 text-lg leading-relaxed mb-6">
          The Team Leaders of the <strong className="text-[#FFD700]">10 teams with the highest counted sales volume worldwide</strong> from{' '}
          <strong className="text-white">13 September at 23:59</strong> to{' '}
          <strong className="text-white">5 November at 23:59</strong> will win:
        </p>

        <div className="grid gap-4 md:grid-cols-2 mb-8">
          <div className="rounded-xl p-5 bg-black/30 border border-yellow-500/25">
            <div className="text-3xl mb-3">✈️</div>
            <h3 className="text-[#FFD700] font-bold text-lg mb-2">Zimbabwe mine visit</h3>
            <p className="text-gray-200 leading-relaxed">
              A Zimbabwe mine visit with return flights, accommodation and food included.
            </p>
          </div>
          <div className="rounded-xl p-5 bg-black/30 border border-yellow-500/25">
            <div className="text-3xl mb-3">🏆</div>
            <h3 className="text-[#FFD700] font-bold text-lg mb-2">10% team share bonus</h3>
            <p className="text-gray-200 leading-relaxed">
              Additional shares from Aureus equal to 10% of all shares sold by their team during the competition period.
            </p>
          </div>
        </div>

        <p className="text-gray-200 leading-relaxed mb-6">
          Our special packages offer <strong className="text-white">additional shares for shareholders and share opportunities for sponsors</strong>, helping raise capital to get operations back on track and place the remaining available shares.
        </p>

        <div className="rounded-xl p-5 mb-6 bg-yellow-500/10 border border-yellow-500/20">
          <p className="text-[#FFD700] font-semibold mb-2">Trip dates will be announced.</p>
          <p className="text-gray-200 leading-relaxed">
            We’re targeting October or November, subject to our September–November visit schedule across India, South Africa, Zimbabwe, Kenya, Vietnam and Germany.
          </p>
        </div>

        <p className="text-xl md:text-2xl font-bold text-white leading-snug mb-8">
          Rally your team. Claim your place in the global top 10. Zimbabwe awaits!
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          {onPurchaseShares && (
            <button
              type="button"
              onClick={onPurchaseShares}
              className="px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-yellow-500 to-yellow-600 text-black hover:from-yellow-400 hover:to-yellow-500 transition-all"
            >
              Purchase Shares
            </button>
          )}
          {onRallyTeam && (
            <button
              type="button"
              onClick={onRallyTeam}
              className="px-6 py-3 rounded-lg font-semibold border border-yellow-500/40 text-[#FFD700] hover:bg-yellow-500/10 transition-all"
            >
              Rally Your Team
            </button>
          )}
        </div>

        <p className="text-gray-400 text-sm italic mt-8">
          Official terms will confirm the competition year, time zone, tie-break rules, package conditions and travel arrangements.
        </p>
      </div>
    </div>
  );
};

export default ZimbabweAwaitsPage;
